import React, {AnchorHTMLAttributes, forwardRef, ReactNode} from 'react'
import {cx} from '@emotion/css'
import styled from '@emotion/styled'
import {CSSDescriptionType, Size} from 'types/Theme'

import {cssTheme} from '../../../themes'
import {linariaMq} from '../../../utils/breakpoints'
import {Loader} from '../Loader'

const BUTTON_VARIANTS = [
  'primary',
  'secondary',
  'tertiary',
  'ghost',
  'ghostInverse',
  'inverse',
  'danger'
] as const

type ButtonVariantTuple = typeof BUTTON_VARIANTS

export type Variant = ButtonVariantTuple[number]

export type ButtonSize = Exclude<Size, 'xs' | 'xxl'>

export interface Props extends AnchorHTMLAttributes<HTMLAnchorElement> {
  /** Identify the element for selection in integration tests, FullStory, etc. */
  dataId?: string
  /** Button content */
  children?: React.ReactNode
  /** Pass through classname to allow styles overrides */
  className?: string
  /** Button style */
  variant?: Variant
  /** Whether the button is in the loading state */
  loading?: boolean
  /** The button click handler */
  onClick?: (e: React.SyntheticEvent) => void
  /** The button size */
  size?: ButtonSize
  /** The HTML button type */
  type?: 'submit' | 'reset' | 'button' | undefined
  /** Whether the button full width of parent */
  fullWidth?: boolean
  /** Whether the button is  disabled */
  disabled?: boolean
  /** Whether the button is  rounded */
  isRounded?: boolean
  /** An Icon to display on the left side of the input for LTR languages. Inverted on RTL */
  iconStart?: ReactNode
  /** An Icon to display on the right side of the input for LTR languages. Inverted on RTL */
  iconEnd?: ReactNode
  /** To add drop shadow styling */
  isFloating?: boolean
  /** Use larger touch area */
  hasTouchArea?: boolean
  form?: string
  /** Optional aria-label for screen-readers, used for icon-only buttons */
  ariaLabel?: string
  /** Pressed state for buttons acting as a toggle */
  ariaPressed?: React.AriaAttributes['aria-pressed']
  /** Optional style object */
  style?: React.CSSProperties
  /** Optional href for button to act as a link */
  href?: string
  /** Optional target for button to act as a link */
  target?: string
}

/** How far the `hasTouchArea` hit target extends past the visible button on every side */
const TOUCH_AREA_OUTSET = 4

interface ButtonSizeMap {
  size: number
  basePadding: number
  horizontalPadding?: number
  typography: CSSDescriptionType
}

export const buttonSizesMap: Record<ButtonSize, ButtonSizeMap> = {
  sm: {
    size: 24,
    basePadding: 4,
    typography: cssTheme.typography.labelXS
  },
  md: {
    size: 32,
    basePadding: 6,
    typography: cssTheme.typography.labelS
  },
  lg: {
    size: 40,
    basePadding: 8,
    typography: cssTheme.typography.labelM
  },
  xl: {
    size: 48,
    basePadding: 8,
    horizontalPadding: 16,
    typography: cssTheme.typography.labelM
  }
}

const getButtonLayoutStyles = (size: ButtonSize) => `
  &.--size-${size} {
    min-height: ${buttonSizesMap[size].size}px;
    min-width: ${buttonSizesMap[size].size}px;
  }
`

const getContentLayoutStyles = (size: ButtonSize) => {
  return `
    &.--size-${size} {
      min-height: ${buttonSizesMap[size].size}px;
      min-width: ${buttonSizesMap[size].size}px;
      padding: ${
        buttonSizesMap[size].horizontalPadding
          ? `${buttonSizesMap[size].basePadding}px ${buttonSizesMap[size].horizontalPadding}px`
          : `${buttonSizesMap[size].basePadding}px`
      };
      font-size: ${buttonSizesMap[size].typography.fontSize};
      line-height: ${buttonSizesMap[size].typography.lineHeight};
      font-weight: ${buttonSizesMap[size].typography.fontWeight};
      letter-spacing: ${buttonSizesMap[size].typography.letterSpacing};
    }
  `
}

export const StyledButtonContent = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: ${cssTheme.layout.radius.md};

  ${Object.keys(buttonSizesMap)
    .map(size => getContentLayoutStyles(size as ButtonSize))
    .join('')}

  &.rounded {
    border-radius: ${cssTheme.layout.radius.rounded};
  }

  &.--has-floating-shadow {
    box-shadow: ${cssTheme.shadows.floating};
  }
`

const getButtonColorsStyles = (variant: Variant) => {
  const tokens = cssTheme.colors.button[variant]
  return `
  &.--variant-${variant} {
    ${StyledButtonContent} {
      transition: transform 0.25s cubic-bezier(0.2, 0, 0, 1);
      color: ${tokens.default.content};
      background-color: ${tokens.default.background};
      outline: ${variant !== 'tertiary' ? '1.5px' : '1px'} solid ${tokens.default.border};
      outline-offset: ${variant !== 'tertiary' ? '-1.5px' : '-1px'};
    }
    ${linariaMq.desktopXs} {
      &:hover,
      &:focus {
        ${StyledButtonContent} {
          background-color: ${tokens.hover.background};
          outline-color: ${tokens.hover.border};
          color: ${tokens.hover.content};
        }
      }
    }
    &:active {
      ${StyledButtonContent} {
        transform: scale(0.925);
      }
      &:disabled {
        ${StyledButtonContent} {
          transform: none;
        }
      }
    }
    &.--disabled {
      ${StyledButtonContent} {
        color: ${tokens.disabled.content};
        background-color: ${tokens.disabled.background};
        outline-color: ${tokens.disabled.border};
      }
    }
  }
`
}

const StyledButton = styled.button`
  border: 0;
  margin: 0;
  padding: 0;
  user-select: none;
  outline: none;
  position: relative;
  text-decoration: none;
  background: none;
  color: inherit;
  -webkit-appearance: none;
  -webkit-tap-highlight-color: transparent;
  cursor: pointer;

  display: inline-flex;
  align-items: center;
  justify-content: center;

  ${Object.keys(buttonSizesMap)
    .map(size => getButtonLayoutStyles(size as ButtonSize))
    .join('')}

  ${BUTTON_VARIANTS.map(variant => getButtonColorsStyles(variant)).join('')}

  &.--full-width {
    width: 100%;
    ${StyledButtonContent} {
      flex-grow: 1;
    }
  }
  /* Pseudo-element instead of a bigger box, so the layout stays the same. lg and xl are large enough already. */
  &.--has-touch-area.--size-sm::before,
  &.--has-touch-area.--size-md::before {
    content: '';
    position: absolute;
    inset: -${TOUCH_AREA_OUTSET}px;
  }
  &:disabled {
    cursor: not-allowed;
  }
`

export const IconWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
`

export const LabelWrapper = styled.div<{size: ButtonSize}>`
  padding: ${({size}) => `0 ${buttonSizesMap[size].basePadding}px`};
`

export const Button = forwardRef(
  (
    {
      variant = 'primary',
      size = 'lg',
      loading = false,
      disabled = false,
      fullWidth = false,
      isRounded = false,
      className = '',
      dataId = '',
      type = 'button',
      onClick,
      children,
      iconStart,
      iconEnd,
      isFloating = false,
      hasTouchArea = false,
      form,
      ariaLabel,
      ariaPressed,
      style,
      href,
      target
    }: Props,
    ref: React.ForwardedRef<HTMLButtonElement>
  ) => {
    const disabledState = loading || disabled

    return (
      <StyledButton
        ref={ref}
        data-id={dataId}
        className={cx(
          `--size-${size}`,
          `--variant-${variant}`,
          disabled && '--disabled',
          fullWidth && '--full-width',
          hasTouchArea && '--has-touch-area',
          className
        )}
        type={type}
        disabled={disabledState}
        onClick={onClick}
        form={form}
        aria-label={ariaLabel}
        aria-pressed={ariaPressed}
        style={style}
        {...(href ? {href, target, as: 'a'} : {})}
      >
        <StyledButtonContent
          className={cx(
            `--size-${size}`,
            isRounded && 'rounded',
            isFloating && !disabled && '--has-floating-shadow'
          )}
        >
          {loading ? (
            <Loader
              size="sm"
              isCompact
              color={
                cssTheme.colors.button[variant][
                  disabled ? 'disabled' : 'default'
                ].content
              }
            />
          ) : (
            <>
              {iconStart && <IconWrapper>{iconStart}</IconWrapper>}

              {children && <LabelWrapper size={size}>{children}</LabelWrapper>}

              {iconEnd && <IconWrapper>{iconEnd}</IconWrapper>}
            </>
          )}
        </StyledButtonContent>
      </StyledButton>
    )
  }
)

Button.displayName = 'Button'
